/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// react-router-dom components
import { useLocation, NavLink } from "react-router-dom";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import { Typography } from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import MDAvatar from "components/MDAvatar";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

import comparedlyLogo from "assets/images/logos/comparedly.png"

import colors from "assets/theme/base/colors";

const { black, info } = colors;

import AuthService from "services/auth-service";
import UserService from "services/user-service";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

function Sidenav({ color = "info", brand = "", brandName, routes, sidenavHover, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const collapseName = location.pathname.replace("/", "");

  const [userProfile, setUserProfile] = useState(null);
  const [activeWorkspace, setActiveWorkspace] = useState(null);

  useEffect(() => {
    const fetchSidenavData = async () => {
      try {
        const profileResp = await AuthService.getProfile();
        setUserProfile(profileResp.data.attributes);

        const workspacesResp = await UserService.fetchUserWorkspaces();
        const workspaces = Array.isArray(workspacesResp) ? workspacesResp : (workspacesResp.data || []);
        const active = workspaces.find(ws => ws.is_active);
        if (active) {
          setActiveWorkspace(active.workspace_id);
        }
      } catch (error) {
        console.error("Error fetching sidenav footer data:", error);
      }
    };

    fetchSidenavData();

    // Listen for profile/workspace updates
    window.addEventListener("profile-updated", fetchSidenavData);
    return () => window.removeEventListener("profile-updated", fetchSidenavData);
  }, []);

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // A function that sets the mini state of the sidenav.
    function handleMiniSidenav() {
      setMiniSidenav(dispatch, window.innerWidth < 1200);
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    /** 
     The event listener that's calling the handleMiniSidenav function when resizing the window.
    */
    window.addEventListener("resize", handleMiniSidenav);

    // Call the handleMiniSidenav function to set the state with the initial value.
    handleMiniSidenav();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleMiniSidenav);
  }, [dispatch]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;

    if (type === "collapse") {
      returnValue = href ? (
        <Link
          href={href}
          key={key}
          target="_blank"
          rel="noreferrer"
          sx={{ textDecoration: "none" }}
        >
          <SidenavCollapse
            name={name}
            icon={icon}
            active={key === collapseName}
            noCollapse={noCollapse}
            sidenavHover={sidenavHover}
          />
        </Link>
      ) : (
        <NavLink key={key} to={route} state={{ refreshId: Date.now() }}>
          <SidenavCollapse name={name} icon={icon} active={key === collapseName} sidenavHover={sidenavHover} />
        </NavLink>
      );
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display="block"
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }
    return returnValue;
  });

  const renderExampleRoutes = routes.map(
    ({ type, name, icon, title, noCollapse, key, href, route }) => {
      let returnValue;

      if (type === "examples") {
        returnValue = href ? (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
              sidenavHover={sidenavHover}
            />
          </Link>
        ) : (
          <NavLink key={key} to={route} state={{ refreshId: Date.now() }}>
            <SidenavCollapse name={name} icon={icon} active={key === collapseName} sidenavHover={sidenavHover} />
          </NavLink>
        );
      }
      return returnValue;
    }
  );

  return (
    <SidenavRoot
      {...rest}
      variant="permanent"
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode, sidenavHover }}
    >
      {/* <MDBox pt={3} pb={1} px={4} textAlign="center">
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox component={NavLink} to="/" display="flex" alignItems="center">
          {brand && <MDBox component="img" src={brand} alt="Brand" width="2rem" />}
          <MDBox
            width={!brandName && "100%"}
            sx={(theme) => sidenavLogoLabel(theme, { miniSidenav })}
          >
            <MDTypography component="h6" variant="button" fontWeight="medium" color={textColor}>
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox> */}

      <MDBox pt={1.5} pb={1} px={4} textAlign="center">
        {/* Close Button for Sidenav */}
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>

        {/* Logo Display */}
        <MDBox
          component={NavLink}
          to="/"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <MDBox
            display="flex"
            flexDirection="column" // Stack items vertically
            alignItems="center" // Center logo and text horizontally
            justifyContent="center" // Ensure proper vertical alignment
            textAlign="center" // Center text properly
            sx={{ width: "100%" }} // Ensures it fits within the sidebar
          >
            {/* Logo */}
            <MDBox
              component="img"
              src={comparedlyLogo}
              alt="Comparedly Logo"
              sx={{
                width: "3rem", // Fixed size for consistency
                maxWidth: "100%", // Prevents overflow
                height: "auto", // Maintain aspect ratio
                objectFit: "contain", // Ensures no distortion
                // Animation: Move logo down when shrunken to fill void
                transform: (!miniSidenav || sidenavHover) ? "translateY(0)" : "translateY(2.5rem)",
                transition: "transform 0.2s ease",
              }}
            />

            {/* App Name */}
            <Typography
              variant="h3" // Adjusted size for side navbar
              sx={{
                color: info.main,
                fontFamily: "'Righteous', cursive",
                fontWeight: "bold",
                mt: 1, // Adds spacing between logo and text
                whiteSpace: "nowrap", // Prevents text from wrapping
                overflow: "hidden", // Hides overflow if needed
                textOverflow: "ellipsis", // Adds "..." if text overflows
                maxWidth: "90%", // Ensures it fits inside the sidebar
                fontSize: "clamp(1rem, 4vw, 1.5rem)", // Dynamically adjusts font size
                opacity: (!miniSidenav || sidenavHover) ? 1 : 0, // Fade effect
                visibility: (!miniSidenav || sidenavHover) ? "visible" : "hidden", // Accessibility/Interaction
                transition: "opacity 0.2s ease, visibility 0.2s ease", // Smooth transition
              }}
            >
              COMPAREDLY
            </Typography>
          </MDBox>

        </MDBox>
      </MDBox>

      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />
      <List>
        {/* <MDBox display="flex flex-col" alignItems="center">
          <MDTypography color={textColor} variant="body2" fontWeight="medium" pl="1.5rem">
            Examples
          </MDTypography>
          {renderExampleRoutes}
        </MDBox>
        <Divider
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        ></Divider> */}
        {renderRoutes}
      </List>
      <MDBox
        mt="auto"
        p={2}
        mx={2}
        mb={2}
        borderRadius="lg"
        sx={{
          backgroundColor: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)",
          display: "flex",
          flexDirection: "column",
          alignItems: miniSidenav && !sidenavHover ? "center" : "flex-start",
          transition: "all 0.2s ease",
          overflow: "hidden"
        }}
      >
        <MDBox display="flex" alignItems="center" width="100%">
          {userProfile?.profile_image ? (
            <MDAvatar
              src={userProfile.profile_image}
              alt={userProfile.name}
              size="xs"
              sx={{ mr: (miniSidenav && !sidenavHover) ? 0 : 1.5 }}
            />
          ) : (
            <Icon sx={{ color: info.main, mr: (miniSidenav && !sidenavHover) ? 0 : 1.5 }}>person</Icon>
          )}
          {(!miniSidenav || sidenavHover) && (
            <MDTypography variant="button" fontWeight="medium" color={textColor} noWrap>
              {userProfile?.name || "Loading..."}
            </MDTypography>
          )}
        </MDBox>
        {(!miniSidenav || sidenavHover) && (
          <MDBox display="flex" alignItems="center" width="100%" mt={0.5}>
            <Icon sx={{ color: info.main, mr: 1.5, fontSize: "small" }}>work</Icon>
            <MDTypography variant="caption" color={textColor} opacity={0.7} noWrap>
              {activeWorkspace?.name || "No Workplace"}
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
      {/* <MDBox p={2} mt="auto">
        <MDButton
          component="a"
          href="https://www.creative-tim.com/product/material-dashboard-pro-react-nodejs"
          target="_blank"
          rel="noreferrer"
          variant="gradient"
          color={sidenavColor}
          fullWidth
        >
          upgrade to pro
        </MDButton>
      </MDBox> */}
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
// Sidenav.defaultProps = {
//   color: "info",
//   brand: "",
// };

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
